import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase-config';
import './TrafficPracticalAssessment.css';

const ENDPOINT = 'https://australia-southeast1-upskillinstitute-au.cloudfunctions.net/trafficPracticalAssessment';
const labels = { S: 'Satisfactory (S)', NYS: 'Not Yet Satisfactory (NYS)' };
const validId = value => typeof value === 'string' && /^[A-Za-z0-9_-]{1,200}$/.test(value);
class RequestError extends Error {
  constructor(message, status) { super(message); this.status = status; }
}

async function request(payload, user, signal) {
  const token = await user.getIdToken();
  if (signal.aborted || auth.currentUser?.uid !== user.uid) throw new RequestError('Staff sign-in changed. Please reload.', 401);
  const response = await fetch(ENDPOINT, {
    method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload), signal, referrerPolicy: 'no-referrer', cache: 'no-store',
  });
  if (!response.ok) {
    if (payload.action === 'exportPdf') {
      let serverMessage;
      try {
        const data = await response.json();
        if (typeof data?.error === 'string' && data.error.trim()) serverMessage = data.error.trim();
      } catch (_) { /* Keep the status-based fallback for non-JSON failures. */ }
      if (serverMessage) throw new RequestError(serverMessage, response.status);
    }
    throw new RequestError([401, 403].includes(response.status)
      ? 'Staff access denied. Sign in with an authorised staff account, then reload.'
      : response.status === 409 ? 'This assessment has changed. Reload the latest record before continuing.'
        : `Request failed (${response.status}). Nothing has been confirmed. Reload before continuing.`, response.status);
  }
  return response;
}

async function readView(response, assessmentId, bookingId) {
  const view = await response.json();
  const invalid = () => { throw new RequestError('Unexpected server response. Reload before continuing.'); };
  if (!view || !validId(view.assessmentId) || view.mode !== 'staff' || !Number.isInteger(view.revision)
    || !Array.isArray(view.projects) || !view.projects.every(project => Array.isArray(project.criteria))
    || !view.marks || typeof view.marks !== 'object' || typeof view.status !== 'string'
    || (assessmentId && view.assessmentId !== assessmentId) || (bookingId && view.bookingId !== bookingId)) invalid();
  const criteria = view.projects.flatMap(project => project.criteria);
  const allMarked = criteria.length > 0 && criteria.every(item => ['S', 'NYS'].includes(view.marks[item.id]));
  const calculatedOutcome = allMarked ? (criteria.some(item => view.marks[item.id] === 'NYS') ? 'NYS' : 'S') : '';
  // Never present S or a completed record when the returned marks contradict it.
  if ((view.outcome === 'S' && calculatedOutcome !== 'S')
    || (view.status === 'completed' && (!allMarked || view.outcome !== calculatedOutcome))) invalid();
  return view;
}

function Assessment({ id, bookingId, user }) {
  const [view, setView] = useState(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState('');
  const [needsReload, setNeedsReload] = useState(false);
  const [reload, setReload] = useState(0);
  const [notice, setNotice] = useState('');
  const latestId = useRef(id);
  const controller = useRef(null);
  const inFlight = useRef(false);
  const pdfUrl = useRef(null);

  useEffect(() => {
    const current = new AbortController();
    controller.current = current;
    setBusy(true); setError(''); setNotice('');
    const assessmentId = latestId.current;
    const payload = assessmentId ? { action: 'get', assessmentId } : { action: 'create', bookingId };
    request(payload, user, current.signal).then(response => readView(response, assessmentId, bookingId)).then(next => {
      if (current.signal.aborted) return;
      latestId.current = next.assessmentId;
      setView(next); setNeedsReload(false);
    }).catch(failure => {
      if (current.signal.aborted) return;
      setView(null);
      setError(failure instanceof RequestError ? failure.message : 'Unable to load assessment. Check access and connection, then reload.');
    }).finally(() => { if (!current.signal.aborted) setBusy(false); });
    return () => current.abort();
  }, [id, bookingId, user, reload]);

  useEffect(() => () => { if (pdfUrl.current) URL.revokeObjectURL(pdfUrl.current); }, []);
  useEffect(() => {
    const warn = event => {
      if (!busy && !needsReload) return;
      event.preventDefault(); event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [busy, needsReload]);

  const locked = view?.status === 'completed';
  const criteria = view?.projects.flatMap(project => project.criteria) || [];
  const markedCount = criteria.filter(item => ['S', 'NYS'].includes(view.marks[item.id])).length;
  const allMarked = criteria.length > 0 && markedCount === criteria.length;

  async function perform(action, fields = {}) {
    if (!view || busy || inFlight.current || needsReload || (action === 'exportPdf' ? !locked : locked)) return;
    if (action === 'complete' && (!allMarked || !window.confirm('Complete and lock this assessment? NYS marks remain Not Yet Satisfactory. Marks cannot be changed afterwards.'))) return;
    const signal = controller.current.signal;
    inFlight.current = true; setBusy(true); setError(''); setNotice('');
    try {
      const response = await request({ action, assessmentId: view.assessmentId,
        ...(action === 'exportPdf' ? {} : { expectedRevision: view.revision }), ...fields }, user, signal);
      if (signal.aborted) return;
      if (action === 'exportPdf') {
        if (response.headers.get('content-type')?.split(';')[0].trim().toLowerCase() !== 'application/pdf') {
          throw new RequestError('The server did not return a PDF. Please retry the download.');
        }
        const blob = await response.blob();
        if (signal.aborted) return;
        if (!blob.size) throw new RequestError('The PDF was empty. Please retry the download.');
        if (pdfUrl.current) URL.revokeObjectURL(pdfUrl.current);
        pdfUrl.current = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = pdfUrl.current; link.download = 'traffic-practical-assessment.pdf';
        document.body.appendChild(link);
        try { link.click(); } finally { link.remove(); }
        setNotice('PDF download started.');
      } else {
        const next = await readView(response, view.assessmentId, bookingId);
        if (signal.aborted) return;
        if (next.revision < view.revision || (action === 'complete' && next.status !== 'completed')) {
          throw new RequestError('The change was not confirmed. Reload before continuing.');
        }
        setView(next);
        setNotice(action === 'complete' ? 'Assessment completed and locked by the server.' : 'Marks saved and confirmed by the server.');
      }
    } catch (failure) {
      if (signal.aborted) return;
      if (action !== 'exportPdf' || failure.status === 409) setNeedsReload(true);
      if ([401, 403].includes(failure.status)) { setView(null); setNeedsReload(true); }
      setError(failure instanceof RequestError ? failure.message
        : action === 'exportPdf' ? 'Unable to download the PDF. Check your connection and retry.'
          : 'The save could not be confirmed. Check your connection and reload before continuing.');
    } finally {
      inFlight.current = false;
      if (!signal.aborted) setBusy(false);
    }
  }

  return <>
    {error && <p role='alert' className='tpa-error'>{error}</p>}
    <p role='status'>{busy ? 'Waiting for server confirmation...' : notice}</p>
    <button type='button' disabled={busy} onClick={() => setReload(value => value + 1)}>Reload latest record</button>
    {view && <>
      <section className='tpa-card'><h2>{view.firstName} {view.lastName}</h2>
        <p>Cohort: {view.cohort || 'Not provided'} / Practical date: {view.practicalDate || 'Not provided'}</p>
        <p>Outcome: {labels[view.outcome] || 'Not yet determined'} / Revision {view.revision}</p>
        <p>{markedCount} of {criteria.length} applicable criteria marked. Only eligible projects are shown.</p>
        {locked && <><p>Completed. Marks are locked.</p><p>Completed by {view.completedByName || 'staff'}{view.completedAt ? ` on ${view.completedAt}` : ''}</p></>}
      </section>
      {view.projects.map(project => <section className='tpa-card' key={project.id}>
        <h2>Project {project.number}: {project.name}</h2>
        {!locked && <div className='tpa-actions'>{Object.entries(labels).map(([value, label]) =>
          <button key={value} type='button' disabled={busy || needsReload} onClick={() => perform('saveMarks', { projectId: project.id, mark: value })}>Mark all project {project.number} {label}</button>)}</div>}
        {project.criteria.map(item => <article className='tpa-criterion' key={item.id}>
          <h3>{item.number}. {item.text}</h3>
          {item.benchmark && <p className='tpa-prewrap'><strong>Benchmark: </strong>{item.benchmark}</p>}
          <p>Saved mark: <strong>{labels[view.marks[item.id]] || 'Not marked'}</strong></p>
          {!locked && <div className='tpa-actions' role='group' aria-label={`Mark criterion ${item.number}`}>
            {Object.entries(labels).map(([value, label]) => <button key={value} type='button' aria-pressed={view.marks[item.id] === value}
              disabled={busy || needsReload} onClick={() => perform('saveMarks', { criterionId: item.id, mark: value })}>{label}</button>)}
          </div>}
        </article>)}
      </section>)}
      <section className='tpa-card'>
        <h2>{locked ? 'Assessment record' : 'Complete assessment'}</h2>
        {!locked && <>
          <p>Mark every applicable criterion before completing. NYS is a valid completed outcome, not a satisfactory result. Completion locks the record.</p>
          <button type='button' className='tpa-primary' disabled={!allMarked || busy || needsReload} onClick={() => perform('complete')}>Complete assessment</button>
        </>}
        <button type='button' disabled={!locked || busy || needsReload} onClick={() => perform('exportPdf')}>Download PDF</button>
      </section>
    </>}
  </>;
}

export default function TrafficPracticalAssessment() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const id = params.get('id');
  const bookingId = params.get('bookingId');
  const validLink = id ? validId(id) : validId(bookingId);
  const [session, setSession] = useState({ ready: false, user: null, version: 0, error: '' });
  useEffect(() => {
    let active = true;
    const unsubscribe = onAuthStateChanged(auth, user => {
      if (active) setSession(current => ({ ready: true, user, version: current.version + 1, error: '' }));
    }, () => {
      if (active) setSession(current => ({ ready: true, user: null, version: current.version + 1, error: 'Unable to check staff authentication. Please reopen this page.' }));
    });
    return () => { active = false; unsubscribe(); };
  }, []);
  return <main className='traffic-practical'>
    <header className='tpa-header'><p>UPSKILL INSTITUTE / STAFF PRACTICAL ASSESSMENT</p><h1>Traffic practical assessment</h1></header>
    {!validLink && <p role='alert'>Open a valid practical booking or assessment link. A bookingId or assessment id is required.</p>}
    {!session.ready && <p role='status'>Checking staff sign-in...</p>}
    {session.ready && !session.user && <p role='alert'>{session.error || 'Please sign in with your staff account, then reopen this page.'}</p>}
    {validLink && session.ready && session.user && <Assessment key={`${id || ''}:${bookingId || ''}:${session.version}`}
      id={id} bookingId={id ? null : bookingId} user={session.user} />}
  </main>;
}
